// UnmintItemDialog — the escape hatch for a mis-typed mint.
//
// WHY A DELETE EXISTS IN A CATALOG THAT OTHERWISE ONLY GROWS: SKU strings are
// immutable by design, so a typo has no correction. You can fix an item's
// description, its family and both classification axes; you cannot fix
// FF-SRC-02-1 when you meant FF-SCR-02-1, and a permanent typo sitting next to
// the real SKU is how the wrong one gets ordered against.
//
// So the rule is narrow and the copy says it out loud: an item that has never
// been ordered, received, moved, stickered, counted or priced can be deleted.
// Anything else is part of the record and gets deactivated instead.
//
// The server is the authority. The catalog page only offers this on rows it
// believes are clean, and the 409 below is what happens when that belief was a
// moment out of date — which it names, per table, rather than just refusing.

import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { Alert } from '../components/ui/Alert';
import { Dialog } from '../components/ui/Dialog';
import { itemLabel, type ItemNameSource } from '../components/ItemName';

/** Table name from the API's 409 → the words a person uses for that thing. */
const REFERENCE_LABELS: Record<string, string> = {
  purchase_order_lines: 'purchase order line',
  receipt_items: 'received item',
  inventory_transactions: 'inventory movement',
  part_instances: 'tracked unit',
  parts_price_history: 'price change',
  parts_inventory: 'stock record',
  board_return_lines: 'vendor return line',
  bom_lines: 'parts list line',
  boms: 'parts list',
  dronefleet: 'drone',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const UnmintItemDialog: React.FC<{
  /** The item itself, so the dialog names it the way the table did and can
   *  still show the code that is about to stop existing. */
  item: ItemNameSource;
  onCancel: () => void;
  /** Resolves when the item is gone. Rejects with the API error otherwise. */
  onConfirm: () => Promise<void>;
}> = ({ item, onCancel, onConfirm }) => {
  const label = itemLabel(item);
  const [working, setWorking] = useState(false);
  const [references, setReferences] = useState<Record<string, number> | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const run = async () => {
    setWorking(true);
    setMessage(null);
    setReferences(null);
    try {
      await onConfirm();
      // Nothing after this: a success unmounts the dialog from the parent, so
      // clearing `working` here is a state update on a component that is gone.
    } catch (err: any) {
      const body = err?.data?.error;
      if (body?.code === 'has_history' && body.references) setReferences(body.references);
      setMessage(body?.message || err?.message || 'That item could not be deleted.');
      setWorking(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      busy={working}
      size="md"
      tone="danger"
      label={`Delete ${label}`}
      title={`Delete ${label}?`}
      // Named, then coded. A confirmation says the thing back in the words
      // the person used, and the SKU underneath is what makes sure it is
      // the right one of two items with similar names.
      meta={item.sku && item.sku !== label ? <span className="font-mono">{item.sku}</span> : undefined}
      problem={message && !references ? message : undefined}
      hideClose
      footer={
        <>
          {/* First, so a question about something destructive starts on the safe key. */}
          <Button variant="quiet" onClick={onCancel}>
            {references ? 'Close' : 'Keep it'}
          </Button>
          <Appear show={!references} as="div">
            <Button variant="danger" onClick={run} disabled={working}>
              {working ? 'Deleting…' : 'Delete it'}
            </Button>
          </Appear>
        </>
      }
    >
      <div className="space-y-3">
        <p className="m-0 text-sm text-on-surface-variant">
          This removes the item from the catalog for good. Only an item that has never been ordered,
          received or counted can be deleted, which is the one safe way to undo a SKU typed wrong.
        </p>

        <Reveal show={!!references}>
        {references && (
          <Alert tone="warning" variant="banner" title="This item has been used:">
            <ul className="m-0 list-disc pl-4 text-xs">
              {Object.entries(references).map(([table, count]) => (
                <li key={table}>{plural(count, REFERENCE_LABELS[table] || table.replace(/_/g, ' '))}</li>
              ))}
            </ul>
            <p className="m-0 mt-1 text-xs">
              Deactivate it instead, and it stops appearing where new work is entered.
            </p>
          </Alert>
        )}
        </Reveal>
      </div>
    </Dialog>
  );
};

export default UnmintItemDialog;
